import { ArrowUp } from 'lucide-react';
import { useRouter } from '@uirouter/react';

import { EnvironmentId } from '@/react/portainer/environments/types';
import { notifySuccess } from '@/portainer/services/notifications';
import { useUpdateHelmReleaseMutation } from '@/react/kubernetes/helm/helmReleaseQueries/useUpdateHelmReleaseMutation';
import {
  HelmRelease,
  UpdateHelmReleasePayload,
} from '@/react/kubernetes/helm/types';

import { LoadingButton } from '@@/buttons';
import { TooltipWithChildren } from '@@/Tip/TooltipWithChildren';

import { openUpgradeHelmModal } from './UpgradeHelmModal';
import { VersionStatusMessage } from './VersionStatusMessage';

export function UpgradeButton({
  environmentId,
  releaseName,
  namespace,
  release,
  updateRelease,
}: {
  environmentId: EnvironmentId;
  releaseName: string;
  namespace: string;
  release?: HelmRelease;
  updateRelease: (release: HelmRelease) => void;
}) {
  const router = useRouter();
  const updateHelmReleaseMutation = useUpdateHelmReleaseMutation(environmentId);

  if (!release) {
    return null;
  }

  const isPending = release?.info?.status?.startsWith('pending');

  const upgradeButton = (
    <LoadingButton
      color="secondary"
      data-cy="k8sApp-upgradeHelmChartButton"
      onClick={handleUpgrade}
      disabled={release?.info?.status?.startsWith('pending')}
      loadingText="Upgrading..."
      isLoading={updateHelmReleaseMutation.isLoading}
      icon={ArrowUp}
      size="medium"
    >
      Edit/Upgrade
    </LoadingButton>
  );

  if (isPending) {
    return (
      <div className="relative">
        <TooltipWithChildren
          message="Cannot edit/upgrade while the Helm release is pending. Please wait for the upgrade to complete, or rollback."
          position="top"
        >
          <span>{upgradeButton}</span>
        </TooltipWithChildren>
        <VersionStatusMessage release={release} />
      </div>
    );
  }

  return (
    <div className="relative">
      {upgradeButton}
      <VersionStatusMessage release={release} />
    </div>
  );

  async function handleUpgrade() {
    if (!release) {
      return;
    }

    const submittedUpgradeValues = await openUpgradeHelmModal(release);

    if (submittedUpgradeValues) {
      upgrade(submittedUpgradeValues, release);
    }

    function upgrade(payload: UpdateHelmReleasePayload, release?: HelmRelease) {
      if (release?.info) {
        const updatedRelease = {
          ...release,
          info: {
            ...release.info,
            status: 'pending-upgrade',
            description: 'Preparing upgrade',
          },
        };
        updateRelease(updatedRelease);
      }
      updateHelmReleaseMutation.mutate(payload, {
        onSuccess: () => {
          notifySuccess('Success', 'Helm chart upgraded successfully');
          // set the revision url param to undefined to refresh the page at the latest revision
          router.stateService.go('kubernetes.helm', {
            namespace,
            name: releaseName,
            revision: undefined,
          });
        },
      });
    }
  }
}
